import React, { useState } from 'react';
import CopyLinkButton from './CopyLinkButton';
import { isOptionalHttpUrl } from '../validation';

const RESULT_STATUSES = ['Pass', 'Fail', 'Blocked', 'Skipped'];

/**
 * Presentational view of a single test run and its results.
 *
 * Props:
 *  - runResults: the object returned by GET /testruns/:id
 *  - onBack: optional handler; renders a "back" button when provided
 *  - shareUrl: optional absolute URL; renders a "copy link" button when provided
 *  - onEditResult: optional async (resultId, { status, comment }) => Promise.
 *    When provided, each result row can have its status and/or comment
 *    corrected inline.
 *  - onEditLinks: optional async ({ ticketUrl, prUrl }) => Promise. When
 *    provided, the ticket / PR links can be edited inline from the summary.
 */
function TestRunResultView({ runResults, onBack, shareUrl, onEditResult, onEditLinks }) {
  const [editingId, setEditingId] = useState(null);
  const [draftStatus, setDraftStatus] = useState('');
  const [draftComment, setDraftComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [rowError, setRowError] = useState('');

  const [editingLinks, setEditingLinks] = useState(false);
  const [draftTicketUrl, setDraftTicketUrl] = useState('');
  const [draftPrUrl, setDraftPrUrl] = useState('');
  const [savingLinks, setSavingLinks] = useState(false);
  const [linksError, setLinksError] = useState('');

  if (!runResults) return null;

  const runDisplayName =
    runResults?.name?.trim() ||
    (runResults?.id ? `Test Run #${runResults.id}` : 'Test Run');
  const canEditLinks = typeof onEditLinks === 'function';
  const hasRunSummary = Boolean(
    runResults?.status || runResults?.created_at || runResults?.completed_at ||
    runResults?.ticket_url || runResults?.pr_url || canEditLinks
  );
  const canEdit = typeof onEditResult === 'function';

  const startEditLinks = () => {
    setDraftTicketUrl(runResults.ticket_url || '');
    setDraftPrUrl(runResults.pr_url || '');
    setLinksError('');
    setEditingLinks(true);
  };

  const cancelEditLinks = () => {
    setEditingLinks(false);
    setLinksError('');
  };

  const saveLinks = async () => {
    if (!isOptionalHttpUrl(draftTicketUrl) || !isOptionalHttpUrl(draftPrUrl)) {
      setLinksError('Links must be valid http(s) URLs, or left blank.');
      return;
    }
    setSavingLinks(true);
    setLinksError('');
    try {
      await onEditLinks({
        ticketUrl: draftTicketUrl.trim(),
        prUrl: draftPrUrl.trim(),
      });
      setEditingLinks(false);
    } catch (err) {
      setLinksError(err?.message || 'Could not save the links. Try again.');
    } finally {
      setSavingLinks(false);
    }
  };

  const startEdit = (result) => {
    setEditingId(result.id);
    setDraftStatus(result.status);
    setDraftComment(result.comment || '');
    setRowError('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setRowError('');
  };

  const saveEdit = async () => {
    setSaving(true);
    setRowError('');
    try {
      await onEditResult(editingId, { status: draftStatus, comment: draftComment.trim() || null });
      setEditingId(null);
    } catch (err) {
      setRowError(err?.message || 'Could not save the change. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="results-view">
      {onBack && (
        <button
          className="btn btn-secondary"
          onClick={onBack}
          style={{ marginBottom: '20px' }}
        >
          ← Back
        </button>
      )}

      <div className="results-title-row">
        <h2 className="results-run-title">{runDisplayName}</h2>
        {shareUrl && <CopyLinkButton url={shareUrl} label="Copy link" />}
      </div>

      {hasRunSummary && (
        <div className="results-header">
          {runResults.status && (
            <p><strong>Status:</strong> {runResults.status}</p>
          )}
          {runResults.created_at && (
            <p><strong>Created:</strong> {new Date(runResults.created_at).toLocaleString()}</p>
          )}
          {runResults.completed_at && (
            <p><strong>Completed:</strong> {new Date(runResults.completed_at).toLocaleString()}</p>
          )}

          {!editingLinks && (
            <>
              <p>
                <strong>Ticket:</strong>{' '}
                {runResults.ticket_url ? (
                  <a href={runResults.ticket_url} target="_blank" rel="noopener noreferrer">
                    {runResults.ticket_url}
                  </a>
                ) : (
                  '—'
                )}
              </p>
              <p>
                <strong>PR:</strong>{' '}
                {runResults.pr_url ? (
                  <a href={runResults.pr_url} target="_blank" rel="noopener noreferrer">
                    {runResults.pr_url}
                  </a>
                ) : (
                  '—'
                )}
                {canEditLinks && (
                  <button
                    type="button"
                    className="btn btn-small btn-secondary"
                    style={{ marginLeft: '10px' }}
                    onClick={startEditLinks}
                  >
                    Edit links
                  </button>
                )}
              </p>
            </>
          )}

          {editingLinks && (
            <div className="results-links-edit">
              <label className="form-group" style={{ display: 'block' }}>
                <strong>Ticket link</strong>
                <input
                  type="url"
                  value={draftTicketUrl}
                  onChange={(e) => setDraftTicketUrl(e.target.value)}
                  placeholder="https://…"
                  disabled={savingLinks}
                />
              </label>
              <label className="form-group" style={{ display: 'block' }}>
                <strong>PR link</strong>
                <input
                  type="url"
                  value={draftPrUrl}
                  onChange={(e) => setDraftPrUrl(e.target.value)}
                  placeholder="https://…"
                  disabled={savingLinks}
                />
              </label>
              {linksError && <div className="result-row-error">{linksError}</div>}
              <div className="result-edit-actions">
                <button className="btn btn-small btn-primary" onClick={saveLinks} disabled={savingLinks}>
                  {savingLinks ? 'Saving…' : 'Save'}
                </button>
                <button className="btn btn-small btn-secondary" onClick={cancelEditLinks} disabled={savingLinks}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {runResults.results && runResults.results.length > 0 ? (
        <div className="results-table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Test Case</th>
                <th>Status</th>
                <th>Duration (ms)</th>
                <th>Executed By</th>
                <th>Execution Date</th>
                <th>Details</th>
                {canEdit && <th>Correct</th>}
              </tr>
            </thead>
            <tbody>
              {runResults.results.map((result, idx) => {
                const isEditing = canEdit && editingId === result.id;
                return (
                  <tr key={result.id ?? idx}>
                    <td>{result.test_case_name || 'Unknown'}</td>
                    <td>
                      {isEditing ? (
                        <select
                          className="result-status-select"
                          value={draftStatus}
                          onChange={(e) => setDraftStatus(e.target.value)}
                          disabled={saving}
                          aria-label="Corrected status"
                        >
                          {RESULT_STATUSES.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      ) : (
                        <span className={`status-badge status-${result.status.toLowerCase()}`}>
                          {result.status}
                        </span>
                      )}
                      {isEditing && rowError && (
                        <div className="result-row-error">{rowError}</div>
                      )}
                    </td>
                    <td>{result.duration_ms || '-'}</td>
                    <td>{result.username || '-'}</td>
                    <td>{new Date(result.execution_date).toLocaleString()}</td>
                    <td>
                      {result.detailed_logs && (
                        <details>
                          <summary>View Logs</summary>
                          <pre style={{ background: '#f5f5f5', padding: '10px', borderRadius: '4px', fontSize: '0.85rem', maxHeight: '200px', overflow: 'auto' }}>
                            {result.detailed_logs}
                          </pre>
                        </details>
                      )}
                      {result.error_message && (
                        <details>
                          <summary>Error</summary>
                          <pre style={{ background: '#fff3cd', padding: '10px', borderRadius: '4px', fontSize: '0.85rem', color: '#721c24' }}>
                            {result.error_message}
                          </pre>
                        </details>
                      )}
                      {isEditing ? (
                        <label className="result-comment-edit">
                          <span>Comment</span>
                          <textarea
                            value={draftComment}
                            onChange={(e) => setDraftComment(e.target.value)}
                            placeholder="Optional comment…"
                            rows="2"
                            disabled={saving}
                          />
                        </label>
                      ) : (
                        result.comment && (
                          <details>
                            <summary>Comment</summary>
                            <p className="result-comment-text">{result.comment}</p>
                          </details>
                        )
                      )}
                    </td>
                    {canEdit && (
                      <td>
                        {isEditing ? (
                          <div className="result-edit-actions">
                            <button
                              className="btn btn-small btn-primary"
                              onClick={saveEdit}
                              disabled={saving || (draftStatus === result.status && draftComment === (result.comment || ''))}
                            >
                              {saving ? 'Saving…' : 'Save'}
                            </button>
                            <button
                              className="btn btn-small btn-secondary"
                              onClick={cancelEdit}
                              disabled={saving}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            className="btn btn-small btn-secondary"
                            onClick={() => startEdit(result)}
                            disabled={editingId !== null}
                          >
                            Edit
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="empty-state">No test results yet</p>
      )}
    </div>
  );
}

export default TestRunResultView;
